import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';
import {Spinner} from '../../Spinner';
import {FaIcon} from '../../FaIcon';
import {YesNoModal} from '../../elements/YesNoModal';
import {ClientMemoryApi} from '../../../api/clientMemoryApi';
import {MediaPlanApi} from '../../../api/mediaPlanApi';
import {DriveConnectModal} from '../memory/DriveConnectModal';
import {MediaPlanTabPicker} from './MediaPlanTabPicker';
import {TAB_STRIP_KINDS, isTabsPayload, tabStripHint, tabStripState} from './tabStrip';

// Media Plan Sync F0 (SSOT/Dashbo - Media Plan Sync.md): conectar la planilla de media plan del
// cliente (Google Sheets vía Picker, purpose=MEDIA_PLAN) y listar/sincronizar/reconectar/
// desconectar. Reusa el wizard `DriveConnectModal` con copy y mimes propios, y las clases
// visuales `.cmem-*` de las conexiones de la Memoria. En F1, esta pantalla suma la propuesta
// de cambios (snapshot -> diff -> aprobar -> aplicar).
const PURPOSE = 'MEDIA_PLAN';
const I18N_BASE = 'media_plan.connections';
const STATUS_CLASS = {
    ACTIVE: '-active',
    PAUSED: '-paused',
    ERROR: '-error',
    REVOKED: '-revoked',
};
const NEEDS_RECONNECT = new Set(['ERROR', 'REVOKED']);

// Primera lectura post-connect: el backend la encola solo (enqueue_source_sync al conectar), así
// que una conexión ACTIVA sin lectura previa está "procesando" — se muestra ese estado y se
// pollea hasta que aparezca la lectura (o venza la ventana: si la tarea murió, se cae al estado
// normal con "Leer ahora" habilitado y el cron nocturno la retoma igual).
const isFirstReadRunning = (conn) => conn.status === 'ACTIVE' && !conn.last_synced_at;
const FIRST_READ_POLL_MS = 5000;
const FIRST_READ_MAX_POLLS = 24;   // ~2 minutos

// Fecha corta y localizada, o null si el valor no es una fecha válida (un timestamp ausente o
// corrupto imprimía "Invalid Date" en el strip). El locale sale del idioma activo, no fijo es-AR:
// un usuario en inglés leía dd/mm como mm/dd.
const formatDateTime = (value, locale) => {
    if (!value) return null;
    const date = new Date(value);
    return Number.isFinite(date.getTime())
        ? date.toLocaleString(locale || 'es-AR', {dateStyle: 'short', timeStyle: 'short'})
        : null;
};

// El mes de la solapa que se está leyendo, cuando NO es el del plan que se está armando. Una solapa
// fijada a mano puede ser de otro mes (nada lo impide, y a veces es deliberado), pero si no se dice
// el usuario ve un nombre y no tiene forma de notar el desfasaje — que es la misma clase de fallo
// silencioso que motivó todo este trabajo.
export const mismatchedTabMonth = (scan) => {
    if (!scan || !scan.selected || !scan.target_month) return null;
    const inList = (scan.tabs || []).find((tab) => tab.gid === scan.selected.gid);
    // Fallback al `plan_month` de la propia elegida: si el escaneo quedó desactualizado y esa solapa
    // ya no figura en la lista, buscarla por gid devolvía `undefined` y el aviso desaparecía sin
    // decir nada. Degrada limpio contra un backend que todavía no emita el campo: sin dato, sin
    // aviso — exactamente lo de antes, nunca un error.
    const month = (inList && inList.plan_month) || scan.selected.plan_month;
    return month && month !== scan.target_month ? month : null;
};

// `onConnectionsChanged(connections)` (opcional): el tab lo usa para saber qué conexión está
// activa y montar la vista de propuesta debajo. `onFirstReadRunningChanged(bool)` (opcional):
// avisa si hay una primera lectura en curso (para el estado de la vista de propuesta).
export function MediaPlanConnectionsView({clientId, onConnectionsChanged, onFirstReadRunningChanged,
                                          sourceInfo, pickerOpenFor, pickerPreselectGid,
                                          pickerPreselectNonce, onPickerOpenChange,
                                          onTabScanChanged}) {
    const {t, i18n} = useTranslation();
    const [config, setConfig] = useState(null);
    // Solapa elegida a mano en curso: el pin relee sincrónicamente (~10-15s).
    const [pinningTab, setPinningTab] = useState(false);
    // Lista de solapas traída en vivo (`?refresh=1`): reemplaza al escaneo persistido dentro del
    // selector. Sirve para el caso canónico del rollover — la agencia acaba de crear la solapa del
    // mes nuevo y el escaneo todavía no la conoce.
    const [refreshedScan, setRefreshedScan] = useState(null);
    const [refreshingTabs, setRefreshingTabs] = useState(false);
    // Selector abierto sobre una conexión SIN escaneo (recién creada, o su primera lectura murió): no
    // hay lista que mostrar, así que se pide al abrir. `null` | 'loading' | 'error'. Antes el selector
    // exigía `tab_scan` y «Elegir la solapa» no hacía nada (C6).
    const [pickerLoad, setPickerLoad] = useState(null);
    const pickerFetchedFor = useRef(null);   // conexión cuya lista ya se pidió en esta apertura
    // Número de pedido de la lista de solapas: sólo pinta la respuesta del último. Sube en cada pedido
    // y al abrir o cerrar el selector, así que la respuesta de un selector ya cerrado (o de un pedido
    // anterior de la MISMA conexión) se descarta.
    const tabsRequest = useRef(0);
    const [connections, setConnections] = useState([]);
    const [loading, setLoading] = useState(true);
    const [connectModalOpen, setConnectModalOpen] = useState(false);
    const [syncingId, setSyncingId] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [firstReadPolls, setFirstReadPolls] = useState(0);
    // Re-lectura ("Releer plan") en curso: corre async → guardamos el `last_synced_at` PREVIO de cada
    // conexión releída. Mientras la conexión conserve ese timestamp, la lectura no terminó (se muestra
    // "Leyendo el plan…" y se pollea); cuando avanza, terminó → toast + la propuesta ya se refrescó.
    const [rereadingBaseline, setRereadingBaseline] = useState({});

    const refresh = useCallback(() => {
        return ClientMemoryApi.listConnections(clientId, PURPOSE).then((data) => {
            if (data && Array.isArray(data.connections)) {
                setConnections(data.connections);
                if (onConnectionsChanged) onConnectionsChanged(data.connections);
            }
        });
    }, [clientId, onConnectionsChanged]);

    // El escaneo de solapas viaja DENTRO de la conexión (sólo para media plan), así que la solapa
    // leída ya no depende de que exista una propuesta. Antes colgaba de la propuesta: sin propuesta
    // el strip no mostraba ninguna solapa, justo en el momento en que ese dato es el que explica
    // qué pasó.
    const tabScanOf = (conn) => (conn && conn.tab_scan) || null;

    // Una re-lectura sigue en curso mientras su conexión conserve el `last_synced_at` que tenía al
    // dispararla (el worker async todavía no lo re-escribió). Distinta de la primera lectura (last null).
    const isRereadRunning = (conn) => Object.prototype.hasOwnProperty.call(rereadingBaseline, conn.id)
        && conn.last_synced_at === rereadingBaseline[conn.id];
    // Hay una lectura EN CURSO (primera o re-lectura) → se pollea hasta que aparezca/avance la lectura.
    const readRunning = firstReadPolls < FIRST_READ_MAX_POLLS
        && connections.some((c) => isFirstReadRunning(c) || isRereadRunning(c));

    useEffect(() => {
        if (!readRunning) return undefined;
        const timer = setTimeout(() => {
            refresh().catch(() => {}).finally(() => setFirstReadPolls((count) => count + 1));
        }, FIRST_READ_POLL_MS);
        return () => clearTimeout(timer);
    }, [readRunning, connections, refresh]);

    // Re-lecturas que TERMINARON (el `last_synced_at` avanzó respecto al baseline): limpiar su baseline
    // y avisar. El refresh() del polling ya trajo la lectura nueva (el contenedor re-monta la propuesta).
    useEffect(() => {
        const finished = connections.filter((conn) => (
            Object.prototype.hasOwnProperty.call(rereadingBaseline, conn.id)
            && conn.last_synced_at !== rereadingBaseline[conn.id]
        ));
        if (finished.length === 0) return;
        setRereadingBaseline((base) => {
            const next = {...base};
            finished.forEach((conn) => delete next[conn.id]);
            return next;
        });
        // Feedback HONESTO: antes esto decía siempre "Plan releído." con sólo ver avanzar el
        // timestamp — y el backend lo avanza incluso cuando no leyó nada, o cuando leyó y la solapa
        // no era un plan. Decir "listo" sobre un no-op es lo que hacía que el usuario clickeara
        // cinco veces sin entender por qué la pantalla no cambiaba.
        const scan = tabScanOf(finished[0]);
        if (scan && scan.outcome && scan.outcome !== 'OK') {
            toast.info(t(`${I18N_BASE}.toast.reread_no_proposal`, {
                tab: (scan.selected || {}).title || '',
                interpolation: {escapeValue: false},
            }));
        } else {
            toast.success(t(`${I18N_BASE}.toast.reread_done`));
        }
    }, [connections, rereadingBaseline, t]);

    useEffect(() => {
        if (onFirstReadRunningChanged) onFirstReadRunningChanged(readRunning);
    }, [readRunning, onFirstReadRunningChanged]);

    // La lista en vivo dura lo que dura el selector abierto: dejarla pegada haría que el panel
    // siguiente mostrara solapas de una consulta anterior (el mismo error que ya cometimos con el
    // escaneo local de la vista de propuesta).
    useEffect(() => {
        tabsRequest.current += 1;
        setRefreshedScan(null);
        setPickerLoad(null);
        pickerFetchedFor.current = null;
    }, [pickerOpenFor]);

    // Sin escaneo, la lista se pide al abrir: la misma llamada barata de «Actualizar lista», que
    // re-lista las solapas del archivo sin parsear nada.
    const loadPickerTabs = useCallback((connection) => {
        pickerFetchedFor.current = connection.id;
        const request = ++tabsRequest.current;
        setPickerLoad('loading');
        MediaPlanApi.getTabs(clientId, connection.id, true)
            .then((data) => {
                if (tabsRequest.current !== request) return;
                if (!isTabsPayload(data)) {
                    setPickerLoad('error');
                    return;
                }
                setRefreshedScan(data);
                setPickerLoad(null);
            })
            .catch(() => tabsRequest.current === request && setPickerLoad('error'));
    }, [clientId]);

    useEffect(() => {
        if (!pickerOpenFor || pickerFetchedFor.current === pickerOpenFor) return;
        const connection = connections.find((c) => c.id === pickerOpenFor);
        if (!connection || connection.tab_scan) return;
        loadPickerTabs(connection);
    }, [pickerOpenFor, connections, loadPickerTabs]);

    // El escaneo de la conexión activa sube al contenedor: la vista de propuesta lo necesita para
    // explicar POR QUÉ no hay propuesta, y viene en el mismo fetch que ya hacía el strip.
    useEffect(() => {
        if (!onTabScanChanged) return;
        const active = connections.find((c) => c.status === 'ACTIVE');
        onTabScanChanged(active ? (active.tab_scan || null) : null);
    }, [connections, onTabScanChanged]);

    useEffect(() => {
        let active = true;
        Promise.all([ClientMemoryApi.getDriveConfig(), ClientMemoryApi.listConnections(clientId, PURPOSE)])
            .then(([cfg, list]) => {
                if (!active) return;
                setConfig(cfg || {configured: false});
                if (list && Array.isArray(list.connections)) {
                    setConnections(list.connections);
                    if (onConnectionsChanged) onConnectionsChanged(list.connections);
                }
            })
            .catch(() => active && setConfig({configured: false}))
            .finally(() => active && setLoading(false));
        return () => {
            active = false;
        };
    }, [clientId, onConnectionsChanged]);

    const onConnected = () => {
        setConnectModalOpen(false);
        setFirstReadPolls(0);   // conexión nueva → ventana de "primera lectura" fresca
        refresh();
    };

    const syncConnection = (connection) => {
        setSyncingId(connection.id);
        ClientMemoryApi.syncConnection(connection.id, clientId)
            .then((data) => {
                if (data && data.status === 'OK') {
                    toast.success(t(`${I18N_BASE}.toast.synced`));
                    // Marca la re-lectura EN CURSO: el worker async re-escribirá `last_synced_at` al
                    // terminar. Hasta entonces se muestra "Leyendo el plan…" y se pollea (feedback).
                    setRereadingBaseline((base) => ({
                        ...base, [connection.id]: connection.last_synced_at || null}));
                } else {
                    toast.error(t(`${I18N_BASE}.toast.error`));
                }
                setFirstReadPolls(0);   // el sync manual también corre async → retomar el polling
                return refresh();
            })
            .catch(() => toast.error(t(`${I18N_BASE}.toast.error`)))
            .finally(() => setSyncingId(null));
    };

    // Elegir la solapa a mano (o volver a automático). Relee sincrónicamente: si esa solapa ya se
    // probó antes tiene snapshot, así que la propuesta sale al instante y sin costo de LLM.
    const selectTab = (connection, tabGid) => {
        setPinningTab(true);
        const month = (tabScanOf(connection) || {}).target_month || null;
        MediaPlanApi.selectTab(clientId, connection.id, tabGid, month)
            .then((data) => {
                if (!data || data.status !== 'OK') {
                    // Mensaje PROPIO, no el del backend: volcar `error_message` crudo es el patrón
                    // que el CLAUDE.md marca como riesgo de aislamiento cross-agency (el día que una
                    // capa de abajo inyecte un texto ahí, se muestra tal cual y sin pasar por i18n).
                    toast.error(t(`${I18N_BASE}.tab.toast.error`));
                    return null;
                }
                const scan = data.tab_scan || {};
                if (tabGid === null) toast.success(t(`${I18N_BASE}.tab.toast.auto_restored`));
                else {
                    toast.success(t(`${I18N_BASE}.tab.toast.pinned`, {
                        tab: (scan.selected || {}).title || '',
                        interpolation: {escapeValue: false},
                    }));
                }
                if (onPickerOpenChange) onPickerOpenChange(null);
                return refresh();
            })
            .catch(() => toast.error(t(`${I18N_BASE}.tab.toast.error`)))
            .finally(() => setPinningTab(false));
    };

    // Re-lista las solapas del archivo SIN parsear (1 llamada barata a Sheets): las conocidas
    // conservan su veredicto y las nuevas aparecen como todavía-no-leídas.
    const refreshTabs = (connection) => {
        setRefreshingTabs(true);
        const request = ++tabsRequest.current;
        MediaPlanApi.getTabs(clientId, connection.id, true)
            .then((data) => {
                if (tabsRequest.current !== request) return;   // el selector se cerró o hubo otro pedido
                if (!isTabsPayload(data)) {
                    toast.error(t(`${I18N_BASE}.tab.toast.refresh_error`));
                    return;
                }
                setRefreshedScan(data);
            })
            .catch(() => toast.error(t(`${I18N_BASE}.tab.toast.refresh_error`)))
            .finally(() => setRefreshingTabs(false));
    };

    const confirmDelete = () => {
        if (!deleteTarget) return;
        setDeleting(true);
        ClientMemoryApi.deleteConnection(deleteTarget.id, clientId)
            .then((data) => {
                if (data && data.status === 'OK') toast.success(t(`${I18N_BASE}.toast.disconnected`));
                else toast.error(t(`${I18N_BASE}.toast.error`));
                return refresh();
            })
            .catch(() => toast.error(t(`${I18N_BASE}.toast.error`)))
            .finally(() => {
                setDeleting(false);
                setDeleteTarget(null);
            });
    };

    if (loading) {
        return (
            <div className="box-loader">
                <Spinner color="positive"/>
            </div>
        );
    }

    const mediaPlanEnabled = config && config.configured && config.media_plan && config.media_plan.enabled;
    if (!mediaPlanEnabled) {
        return (
            <div className="cmem-empty">
                <FaIcon iconName="table-cells-large" className="cmem-empty-icon"/>
                <h3>{t(`${I18N_BASE}.not_available_title`)}</h3>
                <p>{t(`${I18N_BASE}.not_available_subtitle`)}</p>
            </div>
        );
    }

    const connectBtn = (
        <button type="button" className="btn -alt" onClick={() => setConnectModalOpen(true)}>
            {t(`${I18N_BASE}.connect_button`)}
        </button>
    );

    // El selector se abre con o sin escaneo: sin él, muestra que está buscando las solapas y las trae.
    const pickerConnection = pickerOpenFor
        ? connections.find((c) => c.id === pickerOpenFor) || null
        : null;
    const pickerScan = pickerConnection ? (refreshedScan || tabScanOf(pickerConnection)) : null;
    const openPicker = (conn) => onPickerOpenChange
        && onPickerOpenChange(pickerOpenFor === conn.id ? null : conn.id);

    return (
        <div className="cmem-connections mp-source-wrap">
            {connections.length === 0 ? (
                <div className="cmem-empty">
                    <FaIcon iconName="table-cells-large" className="cmem-empty-icon"/>
                    <h3>{t(`${I18N_BASE}.empty_title`)}</h3>
                    <p>{t(`${I18N_BASE}.empty_subtitle`)}</p>
                    <div className="cmem-empty-actions">{connectBtn}</div>
                </div>
            ) : (
                <div className="mp-source-group">
                    {/* SOURCE STRIP: la planilla ES el encabezado del panel (la fuente de la
                        propuesta), no un bloque aparte. Las acciones viven en la misma línea:
                        "Releer plan" (re-lee la planilla → la propuesta se refresca sola al cambiar
                        `last_synced_at`) y "Desconectar". Sin disclosure "Gestionar": mostraba un
                        duplicado del strip y solo aportaba Desconectar. Una fila por conexión (en el
                        piloto hay una sola). */}
                    {connections.map((conn) => {
                        const needsReconnect = NEEDS_RECONNECT.has(conn.status);
                        const running = readRunning && (isFirstReadRunning(conn) || isRereadRunning(conn));
                        // Una sola resolución por fila: el escaneo se leía 8 veces y el desfasaje de
                        // mes se recalculaba dos, una en la condición y otra dentro del texto.
                        const scan = tabScanOf(conn);
                        const monthMismatch = mismatchedTabMonth(scan);
                        const strip = tabStripState(scan);
                        const hint = tabStripHint(scan);
                        return (
                            <div key={conn.id} className={`mp-source ${STATUS_CLASS[conn.status] || ''}`}>
                                <FaIcon iconName="table-cells-large" className="mp-source-icon"/>
                                <div className="mp-source-main">
                                    {/* El nombre abre la planilla en Google Sheets (el objeto de
                                        conexión trae `external_id`); si faltara, cae a texto plano. */}
                                    {conn.external_id ? (
                                        <a className="mp-source-name"
                                           href={`https://docs.google.com/spreadsheets/d/${conn.external_id}/edit`}
                                           target="_blank" rel="noopener noreferrer">
                                            {conn.external_label}
                                            <FaIcon iconName="up-right-from-square" className="mp-source-name-ext"/>
                                        </a>
                                    ) : (
                                        <span className="mp-source-name">{conn.external_label}</span>
                                    )}
                                    <span className="mp-source-meta">
                                        <span className={`mp-source-dot ${STATUS_CLASS[conn.status] || ''}`}/>
                                        <span className={`mp-source-status ${STATUS_CLASS[conn.status] || ''}`}>
                                            {t(`${I18N_BASE}.status.${(conn.status || '').toLowerCase()}`,
                                                {defaultValue: conn.status || ''})}
                                        </span>
                                        {' · '}
                                        {running
                                            ? (isRereadRunning(conn)
                                                ? t(`${I18N_BASE}.reread_running`)
                                                : t(`${I18N_BASE}.first_read_running`))
                                            : formatDateTime(conn.last_synced_at, i18n.language)
                                                ? t(`${I18N_BASE}.last_synced`, {
                                                    date: formatDateTime(conn.last_synced_at, i18n.language),
                                                    // Fecha del sistema: sin esto i18next escapa el "/" a &#x2F;.
                                                    interpolation: {escapeValue: false},
                                                })
                                                : t(`${I18N_BASE}.never_synced`)}
                                        {/* ⓘ de cadencia (fechas honestas, 2026-07-30): "última
                                            verificación" ≠ "última lectura de contenido" — el
                                            tooltip explica cuándo revisa y cuándo relee Dashbo. */}
                                        {!running && conn.last_synced_at && (
                                            <span className="info-icon-container mp-source-cadence">
                                                <FaIcon iconName="info-circle"/>
                                                <span className="warning-tooltip -below -rich">
                                                    <span className="warning-tooltip-body">
                                                        <span className="warning-tooltip-text">
                                                            {t(`${I18N_BASE}.read_cadence_tooltip`)}
                                                        </span>
                                                    </span>
                                                </span>
                                            </span>
                                        )}
                                        {conn.status === 'ACTIVE' && sourceInfo
                                            && formatDateTime(sourceInfo.readAt, i18n.language) && (
                                            <>
                                                {' · '}
                                                {t(`${I18N_BASE}.last_change_detected`, {
                                                    date: formatDateTime(sourceInfo.readAt, i18n.language),
                                                    interpolation: {escapeValue: false},
                                                })}
                                            </>
                                        )}
                                    </span>
                                    {/* Línea PROPIA para la solapa leída, no un sexto ítem de la
                                        meta-línea: esa ya concatena 4 cosas con " · " y sumarle
                                        nombre + dato + acción la vuelve una sopa de texto. Es el
                                        eslabón que faltaba entre "Activa ●verde" y "no hay
                                        propuesta": dice QUÉ se lee y si eso sirve. Se ve siempre
                                        que la conexión esté activa, también sin escaneo (C6): una
                                        conexión recién creada es justo cuando hace falta elegir. */}
                                    {conn.status === 'ACTIVE' && (
                                        <span className="mp-source-tab" data-testid="mp-source-tab">
                                            {/* El ícono va con el nombre: con un nombre largo a 390
                                                bajaba solo a su propio renglón. */}
                                            <span className="mp-source-tab-lead">
                                                <FaIcon iconName="table-cells-large" className="mp-source-tab-icon"/>
                                                {strip.kind === TAB_STRIP_KINDS.READING && (
                                                    <span className="mp-source-tab-name" title={strip.tab}>
                                                        {t(`${I18N_BASE}.tab.reading`, {
                                                            tab: strip.tab, interpolation: {escapeValue: false},
                                                        })}
                                                    </span>
                                                )}
                                                {strip.kind === TAB_STRIP_KINDS.NOT_SCANNED && (
                                                    <span className="mp-source-tab-name -none">
                                                        {t(`${I18N_BASE}.tab.not_scanned`)}
                                                    </span>
                                                )}
                                                {strip.kind === TAB_STRIP_KINDS.NONE && (
                                                    <>
                                                        <span className="mp-source-tab-label">
                                                            {t(`${I18N_BASE}.tab.label`)}
                                                        </span>
                                                        <span className="mp-source-tab-name -none">
                                                            {t(`${I18N_BASE}.tab.none`)}
                                                        </span>
                                                    </>
                                                )}
                                            </span>
                                            {/* De dónde salió la elección, o el motivo del descarte
                                                (ámbar: es lo accionable). */}
                                            {hint && t(`${I18N_BASE}.tab.${hint.key}`, {defaultValue: ''}) && (
                                                <span className={`mp-source-tab-hint ${hint.tone === 'warn' ? '-warn' : ''}`}>
                                                    {t(`${I18N_BASE}.tab.${hint.key}`, {
                                                        ...hint.values, defaultValue: '',
                                                        interpolation: {escapeValue: false},
                                                    })}
                                                </span>
                                            )}
                                            {/* Acción EXPLÍCITA (antes era el chip «elegida
                                                automáticamente ▾», que no se leía como botón). */}
                                            <button type="button" className="mp-source-tab-change"
                                                    aria-expanded={pickerOpenFor === conn.id}
                                                    onClick={() => openPicker(conn)}>
                                                {strip.kind === TAB_STRIP_KINDS.READING
                                                    ? t(`${I18N_BASE}.tab.change`)
                                                    : t('media_plan.proposal.empty.action.pick_tab')}
                                            </button>
                                            {/* La solapa que se lee es de OTRO mes que el plan que
                                                se está armando. Puede ser deliberado, pero sin
                                                decirlo el usuario sólo ve un nombre y no tiene cómo
                                                notar el desfasaje — el mismo fallo mudo que este
                                                trabajo vino a eliminar. */}
                                            {monthMismatch && (
                                                <span className="mp-source-tab-mismatch">
                                                    <FaIcon iconName="triangle-exclamation"/>
                                                    {t(`${I18N_BASE}.tab.month_mismatch`, {
                                                        tabMonth: monthMismatch,
                                                        period: scan.target_month,
                                                        interpolation: {escapeValue: false},
                                                    })}
                                                </span>
                                            )}
                                        </span>
                                    )}
                                    {needsReconnect && (
                                        <span className="cmem-connection-hint">{t(`${I18N_BASE}.reconnect_hint`)}</span>
                                    )}
                                    {conn.status === 'ERROR' && conn.last_sync_error && (
                                        <span className="cmem-connection-error">{conn.last_sync_error}</span>
                                    )}
                                </div>
                                {/* Acciones del strip: par primario/secundario en la MISMA escala
                                    (mp-btn-sm) y el MISMO lenguaje -alt (naranja) — el viejo
                                    `.btn -negative` a secas venía en otra altura, otra fuente y
                                    MAYÚSCULAS, y desentonaba con "Releer plan". */}
                                <div className="mp-source-actions">
                                    {needsReconnect ? (
                                        <button type="button" className="btn -alt mp-btn-sm"
                                                onClick={() => setConnectModalOpen(true)}>
                                            {t(`${I18N_BASE}.reconnect`)}
                                        </button>
                                    ) : (
                                        <button type="button" className="btn -alt mp-btn-sm"
                                                disabled={syncingId === conn.id || running}
                                                onClick={() => syncConnection(conn)}>
                                            {syncingId === conn.id || running
                                                ? t(`${I18N_BASE}.syncing`)
                                                : t(`${I18N_BASE}.reread`)}
                                        </button>
                                    )}
                                    <button type="button" className="btn -alt -negative mp-btn-sm"
                                            onClick={() => setDeleteTarget(conn)}>
                                        {t(`${I18N_BASE}.disconnect`)}
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                    {/* Selector de solapa: vive en un MODAL (el porqué, en el docstring del
                        componente). El estado de apertura vive acá arriba porque lo abren dos
                        superficies: el chip del strip y el CTA del empty state. */}
                    {pickerConnection && (
                        <MediaPlanTabPicker
                            scan={pickerScan}
                            externalId={pickerConnection.external_id}
                            preselectGid={pickerPreselectGid}
                            preselectNonce={pickerPreselectNonce}
                            saving={pinningTab}
                            refreshing={refreshingTabs}
                            loading={!pickerScan && pickerLoad !== 'error'}
                            loadError={!pickerScan && pickerLoad === 'error'}
                            onRetryLoad={() => loadPickerTabs(pickerConnection)}
                            onRefresh={() => refreshTabs(pickerConnection)}
                            onConfirm={(gid) => selectTab(pickerConnection, gid)}
                            onRestoreAuto={() => selectTab(pickerConnection, null)}
                            onClose={() => onPickerOpenChange && onPickerOpenChange(null)}
                        />
                    )}

                    {/* Conectar otra planilla: acción rara (un cliente = un media plan), queda como
                        link discreto para no perder la capacidad sin robar protagonismo. */}
                    <button type="button" className="mp-source-add" onClick={() => setConnectModalOpen(true)}>
                        <FaIcon iconName="plus" className="mp-source-add-icon"/>
                        {t(`${I18N_BASE}.connect_another`)}
                    </button>
                </div>
            )}

            {mediaPlanEnabled && (
                <DriveConnectModal
                    isOpen={connectModalOpen}
                    onClose={() => setConnectModalOpen(false)}
                    clientId={clientId}
                    config={config}
                    onConnected={onConnected}
                    purpose={PURPOSE}
                    i18nBase={I18N_BASE}
                    mimeTypes={config.media_plan.supported_mimes}
                />
            )}

            <YesNoModal
                skipRenderButton
                isOpen={!!deleteTarget}
                title={t(`${I18N_BASE}.disconnect_confirm_title`)}
                question={t(`${I18N_BASE}.disconnect_confirm_question`, {
                    name: deleteTarget ? deleteTarget.external_label : '',
                    // El nombre puede tener "&" o "/" → sin esto i18next los muestra como entidad.
                    interpolation: {escapeValue: false},
                })}
                isSaving={deleting}
                onYesAnswer={confirmDelete}
                onNoAnswer={() => setDeleteTarget(null)}
            />
        </div>
    );
}
